import { badgeVariants } from "@/shared/ui/data-display/badge";
import { translateUi } from "@/shared/lib/ui-translate";

type BadgeVariant = NonNullable<
  NonNullable<Parameters<typeof badgeVariants>[0]>["variant"]
>;

interface BadgeConfig {
  text: string;
  variant: BadgeVariant;
}

type BadgeDictionary = Record<string, BadgeConfig>;

const withAliases = (
  base: BadgeDictionary,
  aliases: Record<string, keyof typeof base>,
): BadgeDictionary => {
  const merged: BadgeDictionary = { ...base };
  for (const [alias, target] of Object.entries(aliases)) {
    const cfg = base[target];
    if (cfg) merged[alias] = cfg;
  }
  return merged;
};

const PLATFORM_ROLE_BASE = {
  moder: { text: "litBadgeConfig.moderator", variant: "role-moderator" },
  moderator: { text: "litBadgeConfig.moderator", variant: "role-moderator" },
  siteadmin: { text: "litBadgeConfig.administrator", variant: "role-admin" },
  developer: { text: "litBadgeConfig.razrabotchik", variant: "role-developer" },
  owner: { text: "litBadgeConfig.vladelets", variant: "role-organization" },
  support: { text: "litBadgeConfig.podderzhka", variant: "role-support-platform" },
  streamer: { text: "litBadgeConfig.strimer", variant: "role-streamer" },
  captain: { text: "litBadgeConfig.kapitan", variant: "role-captain" },
  coach: { text: "litBadgeConfig.trener", variant: "role-coach" },
  user: { text: "litBadgeConfig.uchastnik", variant: "role-user" },
  participant: { text: "litBadgeConfig.uchastnik", variant: "role-user" },
  default: { text: "litBadgeConfig.uchastnik", variant: "role-user" },
} satisfies BadgeDictionary;

const ORG_ROLE_BASE = {
  owner: { text: "litBadgeConfig.vladelets", variant: "role-organization" },
  admin: { text: "litBadgeConfig.administrator", variant: "role-admin" },
  moderator: { text: "litBadgeConfig.moderator", variant: "role-moderator" },
  member: { text: "litBadgeConfig.uchastnik", variant: "role-user" },
  streamer: { text: "litBadgeConfig.strimer", variant: "role-streamer" },
  default: { text: "litBadgeConfig.uchastnik", variant: "secondary" },
} satisfies BadgeDictionary;

const LOL_ROLE_BASE = {
  top: { text: "Top", variant: "role-top" },
  jungle: { text: "Jungle", variant: "role-jungle" },
  mid: { text: "Mid", variant: "role-mid" },
  adc: { text: "ADC", variant: "role-adc" },
  support: { text: "Support", variant: "role-support" },
  fill: { text: "Fill", variant: "role-fill" },
  substitute: { text: "Sub", variant: "role-fill" },
  coach: { text: "Coach", variant: "role-coach" },
  analyst: { text: "Analyst", variant: "role-coach" },
  default: { text: "litBadgeConfig.rol", variant: "secondary" },
} satisfies BadgeDictionary;

// A centralized dictionary to map backend values to badge props
const badgeDictionary: Record<string, Record<string, BadgeConfig>> = {
  tournamentStatus: {
    // New status values
    Draft: { text: "litBadgeConfig.chernovik", variant: "secondary" },
    Upcoming: { text: "litBadgeConfig.skoro", variant: "info" },
    RegistrationOpen: { text: "litBadgeConfig.registratsiya", variant: "success" },
    RegistrationClosed: { text: "litBadgeConfig.registratsiyaZakryta", variant: "warning" },
    InProgress: { text: "litBadgeConfig.idet", variant: "live" },
    Ongoing: { text: "litBadgeConfig.idet", variant: "live" },
    // KAN-359: у драфта своя фаза — команд и матчей ещё нет, «Идёт» здесь врало.
    DraftInProgress: { text: "litBadgeConfig.draftIdet", variant: "info" },
    Completed: { text: "litBadgeConfig.zavershen", variant: "completed" },
    Cancelled: { text: "litBadgeConfig.otmenen", variant: "destructive" },
    AwaitingBracket: { text: "litBadgeConfig.ozhidanieSetki", variant: "warning" },
    Unknown: { text: "litBadgeConfig.neizvestno", variant: "secondary" },
    // Legacy values
    draft: { text: "litBadgeConfig.registratsiya", variant: "info" },
    active: { text: "litBadgeConfig.idet", variant: "live" },
    completed: { text: "litBadgeConfig.zavershen", variant: "completed" },
    cancelled: { text: "litBadgeConfig.otmenen", variant: "destructive" },
    default: { text: "litBadgeConfig.status", variant: "secondary" },
  },
  lolRole: withAliases(LOL_ROLE_BASE, {
    jg: "jungle",
    jungle: "jungle",
    supp: "support",
    sup: "support",
    support: "support",
    bot: "adc",
    bottom: "adc",
  }),
  platformRole: withAliases(PLATFORM_ROLE_BASE, {
    participant: "user",
    organization: "owner",
    admin: "siteadmin",
    moderator: "moderator",
  }),
  orgRole: withAliases(ORG_ROLE_BASE, {
    participant: "member",
    user: "member",
  }),
};

/**
 * Gets the badge text and variant for a given type and value.
 * @param type - The type of badge (e.g., 'tournamentStatus', 'lolRole').
 * @param value - The value from the backend (e.g., 'live', 'admin', 'top').
 * @returns An object with the text and variant for the Badge component.
 */
export const getBadgeConfig = (
  type: keyof typeof badgeDictionary,
  key: string,
): BadgeConfig => {
  // Добавляем защиту от undefined/null
  if (!key || key === "null" || key === "undefined") {
    key = "default";
  }

  const typeConfig = badgeDictionary[type];
  if (!typeConfig) {
    console.warn(`getBadgeConfig: неизвестный type "${type}"`);
    return { text: "litBadgeConfig.neizvestno", variant: "secondary" };
  }

  // Дополнительная защита от undefined в конфигурации
  const config = typeConfig[key] || typeConfig["default"];
  if (!config) {
    console.warn(
      `getBadgeConfig: отсутствует конфигурация для type "${type}" и key "${key}"`,
    );
    return { text: "litBadgeConfig.neizvestno", variant: "secondary" };
  }

  // Русские подписи хранятся ключами Ui (lit…) и переводятся здесь; LoL-роли — как есть.
  return config.text.startsWith("lit") ? { ...config, text: translateUi(config.text) } : config;
};

// Helper function to normalize role strings
export const normalizeRoleForConfig = (
  role: string | null | undefined,
): string => {
  if (!role) return "user";
  const normalized = role.toLowerCase().replace(/\s+/g, "");
  if (normalized === "participant") return "user";
  return normalized;
};

/**
 * Человеческая подпись статуса турнира.
 *
 * KAN-367: статус в нескольких местах выводился строкой как есть, и пользователь
 * видел «Ongoing» вместо «Идёт» — на странице команды, в настройках турнира и на
 * экране регистрации. Справочник подписей существовал, но мимо него писали
 * напрямую, поэтому каждый новый статус вылезал сырым в очередном месте.
 */
export function tournamentStatusLabel(status?: string | null): string {
  if (!status) return "—";
  return getBadgeConfig("tournamentStatus", status).text;
}
